// The buffer-local half of the Monaco RenameProvider (see `renameProvider.ts`).
//
// PRD: Ralph/NavigationalFeatures/PRD.md § NAV1 (amended 2026-08-23).
//
// An alias or CTE name has no catalog object behind it, so F2 on one produces a
// REAL `WorkspaceEdit` that Monaco applies to the active model through its
// bulk-edit service. Verified against monaco-editor 0.44
// (`editor/standalone/browser/standaloneServices.js` → `StandaloneBulkEditService.apply`):
// the service resolves `edit.resource` through the model registry — so the
// resource MUST be the live model's own `uri` — and brackets the splice in
// `model.pushStackElement()` on both sides, which is what makes a single native
// Ctrl+Z restore the whole rename.
//
// Rejections travel on Monaco's documented `Rejection` channel: a `rejectReason`
// returned from `provideRenameEdits` is surfaced by the rename controller via
// `INotificationService.info` (`contrib/rename/browser/rename.js`). That is the
// right channel for the collision guard, which can only be evaluated once the
// user has actually typed a name.

import type { editor, languages, Position, IRange, Uri } from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

import { buildLineIndex, rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import type { SqlRange } from '../../sql-grammar/model';

import { trackNavFeature } from '../analytics/trackNavFeature';
import { planBufferLocalRename, type BufferEdit } from './bufferRenamePlan';
import {
  buildBufferContext,
  findBufferRenameTarget,
  parseOptionsForEngine,
  type BufferRenameTarget,
} from './bufferRenameTarget';

/** The slice of a Monaco model the rename provider reads. Kept structural so
 *  unit tests can drive it with a lightweight double. */
export type RenameModelLike = Pick<
  editor.ITextModel,
  'getValue' | 'getOffsetAt' | 'getWordAtPosition'
> & {
  readonly uri?: Uri;
  readonly getVersionId?: () => number;
};

/** The buffer-local binding at `position`, or null when the cursor names
 *  something else (a catalog object, a keyword, a literal). */
export function resolveBufferTarget(
  engineId: DatabaseEngineId,
  model: RenameModelLike,
  position: Position,
): BufferRenameTarget | null {
  const ctx = buildBufferContext(model.getValue(), parseOptionsForEngine(engineId));
  if (!ctx) return null;
  return findBufferRenameTarget(ctx, model.getOffsetAt(position));
}

/** Returns the WorkspaceEdit (possibly a rejection) when the cursor names a
 *  buffer-local binding, or null to let the persisted-object path run. */
export function provideBufferLocalEdits(
  engineId: DatabaseEngineId,
  model: RenameModelLike,
  position: Position,
  newName: string,
): (languages.WorkspaceEdit & { rejectReason?: string }) | null {
  const plan = planBufferLocalRename({
    source: model.getValue(),
    engineId,
    cursorOffset: model.getOffsetAt(position),
    newName,
  });
  switch (plan.status) {
    case 'no-target':
      return null;
    case 'unchanged':
      return { edits: [] };
    case 'invalid-name':
    case 'collision':
      return { edits: [], rejectReason: plan.message };
    case 'ok': {
      trackNavFeature({
        name: 'feature:navfeatures_rename_buffer',
        props: { kind: plan.kind, occurrences: plan.edits.length },
      });
      return { edits: toWorkspaceTextEdits(model, plan.edits) };
    }
  }
}

function toWorkspaceTextEdits(
  model: RenameModelLike,
  edits: readonly BufferEdit[],
): languages.WorkspaceEdit['edits'] {
  const resource = model.uri as Uri;
  const versionId = model.getVersionId?.();
  return edits.map((edit) => ({
    resource,
    textEdit: { range: sqlRangeToMonaco(edit.range), text: edit.newText },
    versionId,
  }));
}

/** 0-indexed resolver range → Monaco's 1-indexed `IRange`. */
export function sqlRangeToMonaco(range: Pick<
  SqlRange,
  'startLine' | 'startColumn' | 'endLine' | 'endColumn'
>): IRange {
  return {
    startLineNumber: range.startLine + 1,
    startColumn: range.startColumn + 1,
    endLineNumber: range.endLine + 1,
    endColumn: range.endColumn + 1,
  };
}

/** Byte offsets → a 1-indexed Monaco range. Uses the buffer's own line index
 *  rather than `model.getPositionAt` so the same conversion serves the unit
 *  tests' lightweight model doubles. */
export function offsetsToRange(
  model: RenameModelLike,
  span: { readonly start: number; readonly end: number },
): IRange {
  const lineIdx = buildLineIndex(model.getValue());
  return sqlRangeToMonaco(rangeFromOffsets(lineIdx, span.start, span.end));
}
